import SyncAltIcon from "@material-ui/icons/SyncAlt";

import { BaseMenu } from "components/base";
import ColorPaletteList from "components/filter/focus/ColorPaletteList";
import { spaceOutColorsForDisplay } from "components/filter/util/spaceOutColorsForDisplay";
import { IColorPalette } from "components/user-settings/models";

import styles from "./ColorPaletteSelector.module.scss";

interface ColorPaletteSelectorProps {
  selectedPalette: IColorPalette | null;
  onPaletteChange: (palette: IColorPalette) => void;
  useCustomPalettes?: boolean;
  placement?: "top" | "bottom" | "left" | "right";
  testId?: string;
  showReverseButton?: boolean;
}

export default function ColorPaletteSelector({
  selectedPalette,
  onPaletteChange,
  useCustomPalettes = true,
  placement = "bottom",
  testId = "color-palette-selector",
  showReverseButton = false
}: ColorPaletteSelectorProps) {
  const displayColors = selectedPalette?.reverse
    ? [...(selectedPalette?.colors ?? [])].reverse()
    : selectedPalette?.colors ?? [];
  // Capped at 7 (rather than the util's default 8) so the strip fits the
  // narrower trigger width without overflowing.
  const previewColors = spaceOutColorsForDisplay(displayColors, 7);

  return (
    <BaseMenu
      data-testid={testId}
      place={placement}
      trigger={
        <div data-testid={`${testId}-trigger`} className={styles.colorSchemeItem}>
          <div className={styles.paletteName}>{selectedPalette?.name}</div>
          <div className={styles.colorSchemeColors}>
            {previewColors.map((color, i) => (
              <div
                key={`${color}-${i}`}
                className={styles.color}
                style={{ backgroundColor: color }}
              />
            ))}
            {showReverseButton && selectedPalette && (
              <button
                type="button"
                aria-label="Reverse palette"
                data-testid={`${testId}-reverse-button`}
                className={styles.reverseButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onPaletteChange({
                    ...selectedPalette,
                    reverse: !selectedPalette.reverse
                  });
                }}>
                <SyncAltIcon fontSize="small" />
              </button>
            )}
          </div>
        </div>
      }>
      {({ closeMenu }) => (
        <ColorPaletteList
          selected={selectedPalette}
          onSelect={(scheme: IColorPalette) => {
            onPaletteChange(scheme);
            closeMenu();
          }}
          onReverse={(scheme: IColorPalette) => {
            onPaletteChange(scheme);
          }}
          useCustomPalettes={useCustomPalettes}
        />
      )}
    </BaseMenu>
  );
}
